import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const invite = params.get("invitacion") || "";
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [errors, setErrors] = useState({});
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        if (form.name.trim().length < 2) e.name = "Escribe tu nombre.";
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Ese correo no parece válido.";
        if (form.password.length < 8) e.password = "Mínimo 8 caracteres.";
        else if (!/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) e.password = "Combina letras y números.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!validate()) return;
        setLoading(true);
        try {
            await register({ ...form, name: form.name.trim(), invite_code: invite || null });
            navigate("/onboarding");
        } catch (err) {
            setError(errMsg(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell
            title="Crea tu cuenta gratis"
            subtitle={invite ? "Te han invitado a un equipo. ¡Bienvenido!" : "Un minuto y tienes tu ruta personalizada."}
            footer={<>¿Ya tienes cuenta? <Link to="/entrar" className="font-semibold text-nm-accent" data-testid="go-login">Entra aquí</Link></>}
        >
            <form onSubmit={submit} className="space-y-4" data-testid="register-form">
                <div className="space-y-1.5">
                    <Label htmlFor="name">Tu nombre</Label>
                    <Input id="name" data-testid="register-name" value={form.name}
                        onChange={(e) => set("name", e.target.value)} placeholder="Nombre y apellidos" required />
                    {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="email">Correo</Label>
                    <Input id="email" type="email" data-testid="register-email" value={form.email}
                        onChange={(e) => set("email", e.target.value)} placeholder="tu@correo.com" required />
                    {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="password">Contraseña</Label>
                    <Input id="password" type="password" data-testid="register-password" value={form.password}
                        onChange={(e) => set("password", e.target.value)} placeholder="Al menos 8 caracteres" required />
                    {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
                </div>
                {invite && (
                    <p className="rounded-lg bg-nm-accent-soft px-3 py-2 text-xs text-nm-accent" data-testid="invite-notice">
                        Invitación aplicada: entrarás directamente en el equipo de quien te invitó.
                    </p>
                )}
                {error && <p className="rounded-lg bg-nm-accent-soft px-3 py-2 text-sm text-nm-accent" data-testid="register-error">{error}</p>}
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
                    {loading ? "Creando cuenta..." : "Crear mi cuenta"}
                </Button>
                <p className="text-xs leading-relaxed text-nm-muted">
                    Al crear tu cuenta aceptas nuestra{" "}
                    <Link to="/privacidad" className="text-nm-accent">política de privacidad</Link>.
                </p>
            </form>
        </AuthShell>
    );
}
